import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageCircle, Send, Sparkles, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useWhatsAppInbox } from "@/contexts/WhatsAppInboxContext";

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatWindow(minutes) {
  if (!minutes || minutes <= 0) return "expired";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function WhatsAppConversation({ leadId, leadName }) {
  const { refresh: refreshInbox } = useWhatsAppInbox();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const markedRef = useRef(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const r = await api.get(`/admin/leads/${leadId}/whatsapp/messages`);
      setData(r.data);
      if (!markedRef.current) {
        markedRef.current = true;
        refreshInbox?.();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [leadId, refreshInbox]);

  useEffect(() => {
    setLoading(true);
    markedRef.current = false;
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [data?.messages?.length]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      await api.post(`/admin/leads/${leadId}/whatsapp/send`, { text: trimmed });
      setText("");
      toast.success("Message sent");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const messages = data?.messages || [];
  const windowOpen = data?.window_open;
  const minutesLeft = data?.minutes_left || 0;

  return (
    <div
      className="flex h-full flex-col"
      data-testid="whatsapp-tab"
    >
      {/* 24h window banner */}
      {windowOpen ? (
        <div
          className="flex items-center gap-2 rounded-t-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800"
          data-testid="window-open"
        >
          <Clock className="h-3.5 w-3.5" />
          <span className="font-semibold">24h window open</span>
          <span className="text-emerald-700">
            · {formatWindow(minutesLeft)} left to reply free-form
          </span>
        </div>
      ) : (
        <div
          className="flex items-center gap-2 rounded-t-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800"
          data-testid="window-expired"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          <span className="font-semibold">Outside 24h window</span>
          <span className="text-amber-700">
            · approved templates only (coming soon)
          </span>
        </div>
      )}

      {/* Conversation thread */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto rounded-b-lg border border-t-0 border-slate-200 bg-[#ECE5DD] p-3"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      >
        {messages.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-500">
            <MessageCircle className="h-6 w-6" />
            <span>No WhatsApp messages yet</span>
          </div>
        ) : (
          messages.map((m) => {
            const isOut = m.direction === "outbound";
            const failed = m.status === "failed";
            return (
              <div
                key={m.id}
                className={`mb-2 flex ${isOut ? "justify-end" : "justify-start"}`}
                data-testid={`wa-msg-${m.direction}`}
              >
                <div
                  className={`max-w-[78%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                    isOut
                      ? failed
                        ? "bg-rose-100 text-rose-900"
                        : "bg-[#DCF8C6] text-slate-800"
                      : "bg-white text-slate-800"
                  }`}
                >
                  {m.ctwa && (
                    <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                      <Sparkles className="h-2.5 w-2.5" /> From Facebook Ad
                      {m.ad_headline ? ` · ${m.ad_headline}` : ""}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">
                    {m.text || (
                      <span className="italic text-slate-400">
                        [{m.message_type || "media"}]
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-slate-500">
                    {isOut && m.sent_by_name && (
                      <span className="text-slate-500">{m.sent_by_name} ·</span>
                    )}
                    <span>{relTime(m.created_at)}</span>
                    {failed && (
                      <span className="font-semibold text-rose-700">· failed</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-2">
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            windowOpen
              ? `Reply to ${leadName?.split(" ")[0] || "lead"} on WhatsApp…`
              : "Window expired — proactive sends require an approved template"
          }
          disabled={!windowOpen || sending}
          className="resize-none border-0 p-2 text-sm focus-visible:ring-0"
          data-testid="wa-composer"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] text-slate-500">
            {windowOpen ? "⌘/Ctrl + Enter to send" : ""}
          </span>
          <Button
            size="sm"
            onClick={send}
            disabled={!windowOpen || sending || !text.trim()}
            className="h-8 gap-1.5 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="wa-send"
          >
            {sending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" /> Send
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
